/**
 * Mapeo puro `inactivityReason` (+ `planId` para el fallback) → descriptor del banner de
 * organización inactiva en OrgLayout. No es lógica de negocio: no decide el estado de la
 * organización, solo traduce un valor ya decidido por el backend a copy/CTA de presentación.
 *
 * Los 5 motivos de dominio (ver VO InactivityReason en el backend) se agrupan en 3 mensajes
 * porque la acción del usuario es la misma dentro de cada grupo:
 * - trial_expired                        → grupo "trial"
 * - subscription_ended | plan_expired    → grupo "plan"
 * - refunded | manual                    → grupo "critical"
 *
 * Un `inactivityReason` nulo o desconocido (dato ausente o un futuro 6º motivo no mapeado)
 * cae al fallback genérico ya existente, preservando el comportamiento vigente.
 */

export const SUPPORT_EMAIL = 'help@klonup.com';

export type InactivityBannerSeverity = 'warn' | 'crit';

export type InactivityBannerCtaAction =
  | { type: 'navigate'; to: string }
  | { type: 'mailto'; to: string };

export interface InactivityBannerDescriptor {
  titleKey: string;
  bodyKey: string;
  ctaKey: string;
  ctaAction: InactivityBannerCtaAction;
  severity: InactivityBannerSeverity;
}

function pricingDestination(planId: string): string {
  return `/pricing?context=${planId === 'trial' ? 'trial-expired' : 'plan-expired'}`;
}

function fallbackDescriptor(planId: string): InactivityBannerDescriptor {
  return {
    titleKey: 'org_inactive.banner_title',
    bodyKey: 'org_inactive.banner_body',
    ctaKey: 'org_inactive.upgrade_plan',
    ctaAction: { type: 'navigate', to: pricingDestination(planId) },
    severity: 'warn',
  };
}

export function inactivityBanner(
  inactivityReason: string | null,
  planId: string,
): InactivityBannerDescriptor {
  switch (inactivityReason) {
    case 'trial_expired':
      return {
        titleKey: 'org_inactive.reason.trial.title',
        bodyKey: 'org_inactive.reason.trial.body',
        ctaKey: 'org_inactive.reason.trial.cta',
        ctaAction: { type: 'navigate', to: '/pricing?context=trial-expired' },
        severity: 'warn',
      };
    case 'subscription_ended':
    case 'plan_expired':
      return {
        titleKey: 'org_inactive.reason.plan.title',
        bodyKey: 'org_inactive.reason.plan.body',
        ctaKey: 'org_inactive.reason.plan.cta',
        ctaAction: { type: 'navigate', to: '/pricing?context=plan-expired' },
        severity: 'warn',
      };
    case 'refunded':
    case 'manual':
      return {
        titleKey: 'org_inactive.reason.critical.title',
        bodyKey: 'org_inactive.reason.critical.body',
        ctaKey: 'org_inactive.reason.critical.cta',
        ctaAction: { type: 'mailto', to: SUPPORT_EMAIL },
        severity: 'crit',
      };
    default:
      return fallbackDescriptor(planId);
  }
}
